import { motion, useAnimationControls, type Variants } from "motion/react";
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import type { RewardImageLayoutSettings, RewardImagesLayoutSettings } from "./animation-tuning";
import { landingPageAssets } from "./assets";
import {
  doubleYellowRevealDingTimeSeconds,
  legendaryRevealChimeTimesSeconds,
} from "./audio-tuning";
import { defaultRewardStaggerEase } from "./easing-curves";

type RewardItemId = "plantSilhouette" | "sign" | "plantBlue" | "doubleYellow" | "legendary";

type RewardItemStyle = CSSProperties & Record<`--${string}`, string>;

const legendaryBloomCount = 3;
const legendarySparkCount = 6;

const rewardItems = [
  {
    id: "plantSilhouette" as const,
    shellClass: "reveal-mystery-row__item-shell--plant-silhouette",
    src: landingPageAssets.reveal.selectedRow.plantSilhouetteQuestion,
  },
  {
    id: "sign" as const,
    shellClass: "reveal-mystery-row__item-shell--sign",
    src: landingPageAssets.reveal.selectedRow.signQuestion,
  },
  {
    id: "plantBlue" as const,
    shellClass: "reveal-mystery-row__item-shell--plant-blue",
    src: landingPageAssets.reveal.selectedRow.plantBlueQuestion,
  },
  {
    id: "doubleYellow" as const,
    shellClass: "reveal-mystery-row__item-shell--double-yellow",
    src: landingPageAssets.reveal.selectedRow.doubleYellowQuestion,
  },
  {
    id: "legendary" as const,
    shellClass: "reveal-mystery-row__item-shell--legendary",
    src: landingPageAssets.reveal.selectedRow.legendaryQuestion,
  },
] satisfies Array<{
  id: RewardItemId;
  shellClass: string;
  src: string;
}>;

const lockedRewardStagger = {
  durationSeconds: 0.54,
  ease: defaultRewardStaggerEase,
  from: { opacity: 0, scale: 1, x: 0, y: 3.05 },
  overshoot: { at: 0.54, scale: 1.09, x: 0, y: -1 },
  settle: { at: 0.7, scale: 1, x: 0, y: 0 },
  staggerSeconds: 0.155,
} as const;

const rewardEntrancePeakSeconds =
  lockedRewardStagger.durationSeconds * lockedRewardStagger.overshoot.at;

function getLayoutStyle(settings: RewardImageLayoutSettings): RewardItemStyle {
  return {
    "--reward-item-rotation": `${settings.rotationDeg}deg`,
    "--reward-item-width": `${settings.widthPercent}%`,
    "--reward-item-x": `${settings.offsetX}%`,
    "--reward-item-y": `${settings.offsetY}%`,
  };
}

function formatCqw(value: number) {
  return `${value}cqw`;
}

function createItemVariants(): Variants {
  return {
    hidden: {
      opacity: lockedRewardStagger.from.opacity,
      scale: lockedRewardStagger.from.scale,
      x: formatCqw(lockedRewardStagger.from.x),
      y: formatCqw(lockedRewardStagger.from.y),
    },
    show: {
      opacity: [lockedRewardStagger.from.opacity, 1, 1, 1],
      scale: [
        lockedRewardStagger.from.scale,
        lockedRewardStagger.overshoot.scale,
        lockedRewardStagger.settle.scale,
        1,
      ],
      transition: {
        duration: lockedRewardStagger.durationSeconds,
        ease: [
          lockedRewardStagger.ease[0],
          lockedRewardStagger.ease[1],
          lockedRewardStagger.ease[2],
          lockedRewardStagger.ease[3],
        ] as [number, number, number, number],
        times: [0, lockedRewardStagger.overshoot.at, lockedRewardStagger.settle.at, 1],
      },
      x: [
        formatCqw(lockedRewardStagger.from.x),
        formatCqw(lockedRewardStagger.overshoot.x),
        formatCqw(lockedRewardStagger.settle.x),
        0,
      ],
      y: [
        formatCqw(lockedRewardStagger.from.y),
        formatCqw(lockedRewardStagger.overshoot.y),
        formatCqw(lockedRewardStagger.settle.y),
        0,
      ],
    },
  };
}

const cueHostVariants: Variants = {
  hidden: { opacity: 1, scale: 1, x: 0, y: 0 },
  show: { opacity: 1, scale: 1, x: 0, y: 0 },
};

type RewardStaggerRowProps = {
  active: boolean;
  entranceDelayMs: number;
  layouts: RewardImagesLayoutSettings;
  /** Bumped to re-run the locked stagger from the animation tuning pane. */
  replayToken: number;
  revealAudioDelayMs: number;
  revealAudioPlaybackRate: number;
};

export function RewardStaggerRow({
  active,
  entranceDelayMs,
  layouts,
  replayToken,
  revealAudioDelayMs,
  revealAudioPlaybackRate,
}: RewardStaggerRowProps) {
  const controls = useAnimationControls();
  const [hasCompletedInitialEntrance, setHasCompletedInitialEntrance] = useState(false);
  const [legendaryCelebrationRun, setLegendaryCelebrationRun] = useState(0);
  const hasPlayedLegendaryCelebrationRef = useRef(false);
  const legendaryCelebrationReplayRef = useRef(replayToken);
  const playRequestIdRef = useRef(0);
  const entranceDelaySeconds = Math.max(entranceDelayMs, 0) / 1000;
  const revealPlaybackRate = Math.max(revealAudioPlaybackRate, 0.01);
  const revealAudioDelaySeconds = Math.max(revealAudioDelayMs, 0) / 1000;
  const doubleYellowDingSeconds =
    revealAudioDelaySeconds + doubleYellowRevealDingTimeSeconds / revealPlaybackRate;
  const legendaryFirstChimeSeconds =
    revealAudioDelaySeconds + legendaryRevealChimeTimesSeconds[0] / revealPlaybackRate;
  const legendarySecondChimeSeconds =
    revealAudioDelaySeconds + legendaryRevealChimeTimesSeconds[1] / revealPlaybackRate;
  const legendaryThirdChimeSeconds =
    revealAudioDelaySeconds + legendaryRevealChimeTimesSeconds[2] / revealPlaybackRate;
  const doubleYellowCueDelaySeconds = Math.max(
    doubleYellowDingSeconds - rewardEntrancePeakSeconds,
    0,
  );
  const legendaryCueDelaySeconds = Math.max(
    legendaryFirstChimeSeconds - rewardEntrancePeakSeconds,
    0,
  );
  const doubleYellowCueStyle: RewardItemStyle = {
    "--reward-cue-delay": `${doubleYellowCueDelaySeconds}s`,
    "--reward-cue-duration": `${lockedRewardStagger.durationSeconds}s`,
  };
  const legendaryEffectStyle: RewardItemStyle = {
    "--legendary-bloom-delay-two": `${legendarySecondChimeSeconds - legendaryFirstChimeSeconds}s`,
    "--legendary-bloom-delay-three": `${legendaryThirdChimeSeconds - legendaryFirstChimeSeconds}s`,
    "--legendary-celebration-duration": `${0.9 / revealPlaybackRate}s`,
    "--legendary-effect-duration": `${0.64 / revealPlaybackRate}s`,
    "--reward-cue-delay": `${legendaryCueDelaySeconds}s`,
    "--reward-cue-duration": `${lockedRewardStagger.durationSeconds}s`,
  };

  const itemVariants = useMemo(() => createItemVariants(), []);

  // Base delay is scene orchestration (wait after pack handoff), not part of the pop itself.
  // Only the first natural entrance uses it; replay / retune / packs-cycle restart instantly.
  const hasPlayedEntranceRef = useRef(false);

  const containerVariants = useMemo<Variants>(
    () => ({
      hidden: {},
      show: {
        transition: {
          // Default for variant definition; actual plays override delayChildren below.
          delayChildren: 0,
          staggerChildren: lockedRewardStagger.staggerSeconds,
        },
      },
    }),
    [],
  );

  // Imperative restart: remount-via-key is unreliable for Motion variant orchestrations
  // that are already at "show". Explicitly reset to hidden, then play show.
  useEffect(() => {
    const requestId = ++playRequestIdRef.current;
    let legendaryCelebrationTimer: ReturnType<typeof setTimeout> | undefined;

    if (!active) {
      if (hasPlayedEntranceRef.current) {
        setHasCompletedInitialEntrance(true);
      }
      void controls.start("hidden");
      return undefined;
    }

    const isReplayOrRetune = replayToken > 0 || hasPlayedEntranceRef.current;
    const delayChildren = isReplayOrRetune ? 0 : entranceDelaySeconds;
    const shouldPlayLegendaryCelebration =
      !hasPlayedLegendaryCelebrationRef.current ||
      replayToken !== legendaryCelebrationReplayRef.current;

    if (shouldPlayLegendaryCelebration) {
      legendaryCelebrationTimer = setTimeout(() => {
        if (requestId === playRequestIdRef.current) {
          hasPlayedLegendaryCelebrationRef.current = true;
          legendaryCelebrationReplayRef.current = replayToken;
          setLegendaryCelebrationRun((current) => current + 1);
        }
      }, legendaryFirstChimeSeconds * 1000);
    }

    void (async () => {
      await controls.set("hidden");
      if (requestId !== playRequestIdRef.current) {
        return;
      }
      await controls.start("show", {
        delayChildren,
        staggerChildren: lockedRewardStagger.staggerSeconds,
      });
      if (requestId === playRequestIdRef.current) {
        hasPlayedEntranceRef.current = true;
      }
    })();

    return () => {
      if (legendaryCelebrationTimer) {
        clearTimeout(legendaryCelebrationTimer);
      }
    };
  }, [active, controls, entranceDelaySeconds, legendaryFirstChimeSeconds, replayToken]);

  return (
    <motion.div
      animate={controls}
      aria-hidden="true"
      className="reveal-mystery-row"
      initial="hidden"
      variants={containerVariants}
    >
      {rewardItems.map((item) => (
        <motion.span
          className={`reveal-mystery-row__item-shell ${item.shellClass}`}
          key={item.id}
          style={getLayoutStyle(layouts[item.id])}
          variants={
            !hasCompletedInitialEntrance && (item.id === "doubleYellow" || item.id === "legendary")
              ? cueHostVariants
              : itemVariants
          }
        >
          {item.id === "legendary" ? (
            <span
              className={
                hasCompletedInitialEntrance
                  ? "reward-stagger-content"
                  : `reward-audio-cue${active ? " reward-audio-cue--active" : ""}`
              }
              key={`legendary-cue-${replayToken}`}
              style={legendaryEffectStyle}
            >
              <span
                className={`legendary-stage${legendaryCelebrationRun > 0 ? " legendary-stage--celebrating" : ""}`}
                key={`legendary-stage-${legendaryCelebrationRun}`}
              >
                <span aria-hidden="true" className="legendary-blooms">
                  {Array.from({ length: legendaryBloomCount }, (_, index) => (
                    <span
                      className={`legendary-bloom legendary-bloom--${index + 1}`}
                      key={`legendary-bloom-${index + 1}`}
                    />
                  ))}
                </span>
                <span className="legendary-media">
                  <img alt="" className="reveal-mystery-row__item legendary-base" src={item.src} />
                </span>
                <span aria-hidden="true" className="legendary-sparkles">
                  {Array.from({ length: legendarySparkCount }, (_, index) => (
                    <img
                      alt=""
                      className={`legendary-spark legendary-spark--${index + 1}`}
                      key={`legendary-spark-${index + 1}`}
                      src={landingPageAssets.reveal.selectedRow.legendaryStar}
                    />
                  ))}
                </span>
              </span>
            </span>
          ) : item.id === "doubleYellow" ? (
            <span
              className={
                hasCompletedInitialEntrance
                  ? "reward-stagger-content"
                  : `reward-audio-cue${active ? " reward-audio-cue--active" : ""}`
              }
              key={`double-yellow-cue-${replayToken}`}
              style={doubleYellowCueStyle}
            >
              <img alt="" className="reveal-mystery-row__item" src={item.src} />
            </span>
          ) : (
            <img alt="" className="reveal-mystery-row__item" src={item.src} />
          )}
        </motion.span>
      ))}
    </motion.div>
  );
}
